<template>
    <div>
        <div class="registration-card__grid">
            <div class="registration-card__wrapper">
                <PreviousStep />
                <h3 class="registration-card__title">{{ currentStep.title }}</h3>

                <p>
                    Please enter your vehicle registration below. This is so we can check
                    how much it would cost for your vehicle in the clean air zone.
                </p>

                <div class="registration-card__card">
                    <form @submit.prevent="check" method="GET" action="/api/clean-air-zone/vehicle-location" class="registration-card__inner">
                        <p class="registration-card__heading">
                            Please enter your registration
                        </p>

                        <div class="registration-card__input-group form-group">
                            <input
                                v-model="registration"
                                class="registration-card__input form-control form-control--text"
                                type="text"
                                placeholder="Enter Registration"
                            />
                        </div>
                        <div class="bg-danger validation-error animated slideInDown" v-if="error !== false" v-html="error"></div>

                        <div class="registration-card__content">
                            <LoadingSpinner v-if="loading" />
                            <button
                                v-else
                                class="btn btn--primary registration-card__button"
                                type="submit"
                            >
                                Continue
                            </button>
                        </div>
                    </form>
                </div>
            </div>
            <div class="registration-card__location-details" ref="mapEl"></div>
        </div>
    </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import PreviousStep from "../PreviousStep";
import LoadingSpinner from "../../../../core/components/LoadingSpinner.vue";
import { customEvent } from "../../../../modules/tracking/AnalyticsEvents";

export default {
    components: {
        PreviousStep,
        LoadingSpinner
    },
    data() {
        return {
            error: false,
            loading: false,
        };
    },
    computed: {
        ...mapState(["location"]),
        ...mapGetters(["currentStep"]),
        registration: {
            get() {
                return this.$store.state.registration;
            },
            set(value) {
                this.$store.commit("setRegistration", value);
            },
        },
    },
    methods: {
        check() {
            if (!this.registration) {
                this.error = "Please enter your registration";
                return;
            }
            this.loading = true;
            this.error = false;
            this.$store.dispatch("fetchVehicleLocation")
            .then(() => {
                this.loading = false;
                customEvent({
                    category: "Clean Air Zone",
                    action: "{$currentStep.step} of {$currentStep.total}",
                    label: "Clicked",
                });
                this.$store.dispatch("navigatorStep", this.currentStep.next);
            })
            .catch(error => {
                this.loading = false;
                this.error = 'Sorry, that registration was not recognized';
            })
        },
        initMap(el) {
            window.GoogleMapsLoader.getMap().then((google) => {

                const map = new google.maps.Map(el, {
                    center: { lat: 51.5033998, lng: -0.1277265 },
                    zoom: 12,
                });

                map.setOptions({
                    zoomControl: false,
                    mapTypeControl: false,
                    scaleControl: false,
                    streetViewControl: false,
                    rotateControl: false,
                    fullscreenControl: false,
                });

                if (this.location) {
                    map.setCenter(this.location);

                    const markerData = {
                        position: this.location,
                        map: map,
                    };
                    const marker = new google.maps.Marker(markerData);
                }

                const files = [
                    "/js/clean-air-zone/Ultra_Low_Emission_Zone_(ULEZ).geojson",
                    "/js/clean-air-zone/Bath CAZ 2020.geoJSON",
                    "/js/clean-air-zone/Birmingham CAZ 2020.geoJSON",
                    "/js/clean-air-zone/Bradford CAZ 2021.geoJSON",
                    "/js/clean-air-zone/Bristol CAZ 2021.geoJSON",
                    "/js/clean-air-zone/Newcastle CAZ 2021.geoJSON",
                    "/js/clean-air-zone/Portsmouth CAZ 2021.geoJSON",
                    "/js/clean-air-zone/Sheffield CAZ.geoJSON",
                    "/js/clean-air-zone/London CCZ_ULEZ 2019.geoJSON",
                ];

                files.forEach(file => {
                    map.data.loadGeoJson(file);
                })
                map.data.setStyle(function(feature) {
                    return {
                        strokeColor: feature.getProperty('stroke'),
                        strokeOpacity: feature.getProperty('stroke-opacity'),
                        strokeWeight: feature.getProperty('stroke-width'),
                        fillColor: feature.getProperty('fill'),
                        fillOpacity: feature.getProperty('fill-opacity'),
                    }
                });
            });
        },
    },
    mounted() {
        window.scrollTo(0, 0);
        this.initMap(this.$refs.mapEl);
    },
};
</script>

<style lang="scss" scoped>
.registration-card {
    &__grid {
        display: grid;
        grid-template-columns: 1fr;
        gap: 15px;
    }

    @media (min-width: 768px) {
        &__grid {
            grid-template-columns: 2fr 1fr;
        }
    }

    &__wrapper {
        display: flex;
        flex-direction: column;
    }

    &__card {
        box-shadow: 0px 11px 24px rgba(0, 0, 0, 0.07);
        border-radius: 10px;
        border-top: 10px solid #0293d6;
        margin-left: auto;
        margin-right: auto;
        width: 100%;
        height: min-content;
        flex-grow: 1;
    }

    &__title {
        font-weight: 700;
        font-size: 20px;
        line-height: 20px;
        color: #192a44;
    }

    &__inner {
        display: grid;
        grid-template-columns: 1fr;
        align-items: center;
        padding: 25px;
        background-color: white;
        border-radius: 0 0 10px 10px;
        height: 100%;
    }

    &__heading {
        font-weight: 700;
        font-size: 16px;
        line-height: 24px;
        color: #4a4a4a;
    }

    &__input {
        background-color: #f7f7f7;
    }

    &__button {
        margin-top: 20px;
        min-width: 100%;
    }

    @media (min-width: 768px) {
        &__button {
            margin-top: 38px;
            min-width: 295px;
        }
    }

    &__location-details {
        display: none;
    }

    @media (min-width: 768px) {
        &__location-details {
            display: block;
            margin-top: 15px;
            border-radius: 10px;
        }
    }
}
</style>
